import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import Loader from '../../components/common/Loader';
import BadgeStatus from '../../components/common/BadgeStatus';
import Pagination from '../../components/common/Pagination';
import { getDefaultItemsPerPageSync, getDefaultItemsPerPage } from '../../utils/settings';
import '../../styles/Table.css';
import '../../styles/Modals.css';

// Memoized table component - only re-renders when handovers data changes
const HandoversTable = memo(({
  handovers,
  loading,
  onRowClick,
  onSendReminder,
  formatDate,
  formatDateTime,
  getStatusLabel
}) => {
  if (loading && handovers.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '14px', color: '#718096' }}>Loading...</div>
      </div>
    );
  }

  if (handovers.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#a0aec0', fontSize: '13px' }}>
        No handovers found
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1200px' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
            <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096' }}>Employee</th>
            <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096' }}>Asset Tag ID</th>
            <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096' }}>Description</th>
            <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096' }}>Assigned Date</th>
            <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096' }}>Assigned By</th>
            <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096' }}>Acknowledgement Status</th>
            <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096' }}>Acknowledgement Date</th>
            <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096' }}>Comment</th>
            <th style={{ textAlign: 'center', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096', width: '120px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {handovers.map((handover) => {
            const handoverId = handover.id || handover._id;
            const statusLabel = getStatusLabel(handover.acknowledgementStatus, handover.issues, handover.handoverType, handover.asset?.status);
            return (
              <tr
                key={handoverId}
                style={{
                  borderBottom: '1px solid #e2e8f0',
                  cursor: 'pointer'
                }}
                onClick={() => onRowClick(handover)}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafc'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                <td style={{ padding: '10px', color: '#4a5568', fontSize: '13px' }}>
                  {handover.assignedTo?.name || 'N/A'}
                </td>
                <td style={{ padding: '10px', fontWeight: 600, color: '#2d3748', fontSize: '13px' }}>
                  {handover.asset?.assetTag || 'N/A'}
                </td>
                <td style={{ padding: '10px', color: '#4a5568', fontSize: '13px' }}>
                  {handover.asset?.description || 'N/A'}
                </td>
                <td style={{ padding: '10px', color: '#718096', fontSize: '13px' }}>
                  {formatDate(handover.assignedDate)}
                </td>
                <td style={{ padding: '10px', color: '#718096', fontSize: '13px' }}>
                  {handover.assignedBy?.name || 'N/A'}
                </td>
                <td style={{ padding: '10px' }}>
                  <BadgeStatus
                    status={statusLabel}
                    variant={
                      statusLabel === 'Received' ? 'info' :
                        statusLabel === 'In Transfer' ? 'secondary' :
                          statusLabel === 'pending' ? 'warning' :
                            'danger'
                    }
                  />
                </td>
                <td style={{ padding: '10px', color: '#718096', fontSize: '13px' }}>
                  {handover.acknowledgedAt ? formatDateTime(handover.acknowledgedAt) : '-'}
                </td>
                <td style={{ padding: '10px', maxWidth: '200px' }}>
                  {handover.comments ? (
                    <span
                      title={handover.comments}
                      style={{
                        color: '#4a5568',
                        fontSize: '12px',
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {handover.comments.length > 30 ? handover.comments.substring(0, 30) + '...' : handover.comments}
                    </span>
                  ) : (
                    <span style={{ color: '#a0aec0', fontSize: '13px' }}>-</span>
                  )}
                </td>
                <td style={{ padding: '10px', textAlign: 'center' }}>
                  {statusLabel !== 'Received' && statusLabel !== 'Issue Reported' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSendReminder(handover);
                      }}
                      style={{
                        padding: '6px 12px',
                        background: '#edf2f7',
                        color: '#4a5568',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontWeight: 500
                      }}
                      title="Send Reminder Email"
                    >
                      📧 Remind
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
});

HandoversTable.displayName = 'HandoversTable';

const AcknowledgementsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [handovers, setHandovers] = useState([]);
  const [totalHandovers, setTotalHandovers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedHandover, setSelectedHandover] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(getDefaultItemsPerPageSync());
  
  // Fetch settings on mount and update itemsPerPage if needed
  useEffect(() => {
    const updateItemsPerPage = async () => {
      const defaultItems = await getDefaultItemsPerPage();
      if (defaultItems !== itemsPerPage) {
        setItemsPerPage(defaultItems);
      }
    };
    updateItemsPerPage();
  }, []);

  const [filters, setFilters] = useState({
    employee: '',
    site: '',
    location: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    search: '',
  });

  // Separate state for search input to prevent focus loss
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    fetchEmployees();
    fetchSites();
    fetchLocations();
  }, []);

  // Debounce search input - only update filters after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch handovers when filters, search, or pagination changes - all handled by backend
  useEffect(() => {
    fetchHandovers();
  }, [currentPage, itemsPerPage, filters]);

  const fetchHandovers = async () => {
    try {
      setLoading(true);

      // Build query parameters - all filtering and pagination handled by backend
      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };

      // Add filter parameters
      if (filters.employee) {
        params.assignedTo = filters.employee;
      }
      if (filters.status) {
        params.acknowledgementStatus = filters.status;
      }
      if (filters.site) {
        params.site = filters.site;
      }
      if (filters.location) {
        params.location = filters.location;
      }
      if (filters.dateFrom) {
        params.dateFrom = filters.dateFrom;
      }
      if (filters.dateTo) {
        params.dateTo = filters.dateTo;
      }
      if (filters.search && filters.search.trim()) {
        params.search = filters.search.trim();
      }

      const response = await api.getHandovers(params);
      if (response.success) {
        setHandovers(response.data || []);
        setTotalHandovers(response.total || 0);
        setTotalPages(response.totalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching handovers:', error);
      toast.error('Error fetching handovers');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.getUsers({ role: 'employee', limit: 1000 });
      if (response.success) {
        setEmployees(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await api.getSites({ isActive: 'true' });
      if (response.success) {
        setSites(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await api.getLocations({ isActive: 'true' });
      if (response.success) {
        setLocations(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleFilterChange = useCallback((name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to page 1 when filter changes
  }, []);

  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters({
      employee: '',
      site: '',
      location: '',
      status: '',
      dateFrom: '',
      dateTo: '',
      search: '',
    });
    setSearchInput('');
    setCurrentPage(1);
  }, []);

  // Calculate active filters count (including search input)
  const activeFiltersCount = useMemo(() => {
    const filterCount = Object.entries(filters).filter(([key, value]) =>
      key !== 'search' && value !== '' && value !== null && value !== undefined
    ).length;
    return filterCount + (searchInput ? 1 : 0);
  }, [filters, searchInput]);

  const handleRowClick = useCallback(async (handover) => {
    try {
      const response = await api.getHandover(handover.id || handover._id);
      if (response.success) {
        setSelectedHandover(response.data);
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('Error fetching handover details:', error);
    }
  }, []);

  const handleSendReminder = useCallback(async (handover) => {
    const employeeEmail = handover.assignedTo?.email;
    if (!employeeEmail) {
      toast.error('Employee email not found');
      return;
    }

    try {
      await api.sendHandoverReminder(handover.id || handover._id);
      toast.success(`Reminder email sent to ${employeeEmail}`);
    } catch (error) {
      console.error('Error sending reminder email:', error);
      toast.error('Failed to send reminder email');
    }
  }, []);

  const getStatusLabel = useCallback((status, issues, handoverType, assetStatus) => {
    if (issues) return 'Issue Reported';
    if (status === 'acknowledged') return 'Received';
    // Check if it's a transfer with pending status or asset is in_transfer
    if (status === 'pending' && (handoverType === 'transfer' || assetStatus === 'in_transfer')) {
      return 'In Transfer';
    }
    if (status === 'pending') return 'pending';
    return status || 'pending';
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }, []);

  const formatDateTime = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // Memoize summary cards to prevent re-renders
  const summaryCards = useMemo(() => {
    const pendingCount = handovers.filter(h => h.acknowledgementStatus === 'pending').length;
    const receivedCount = handovers.filter(h => h.acknowledgementStatus === 'acknowledged').length;
    const issuesCount = handovers.filter(h => h.acknowledgementStatus === 'issue_reported' || h.issues).length;

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
        <div style={{
          background: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '11px', color: '#718096', marginBottom: '6px' }}>Total Assignments</div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#2d3748' }}>{totalHandovers}</div>
        </div>
        <div style={{
          background: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '11px', color: '#718096', marginBottom: '6px' }}>Pending</div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#ed8936' }}>
            {pendingCount}
          </div>
        </div>
        <div style={{
          background: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '11px', color: '#718096', marginBottom: '6px' }}>Received</div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#38a169' }}>
            {receivedCount}
          </div>
        </div>
        <div style={{
          background: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '11px', color: '#718096', marginBottom: '6px' }}>Issues Reported</div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#e53e3e' }}>
            {issuesCount}
          </div>
        </div>
      </div>
    );
  }, [handovers, totalHandovers]);

  return (
    <div className="acknowledgements-page" style={{ padding: '16px' }}>
      {/* Filters */}
      <div style={{
        background: 'white',
        padding: '16px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#2d3748' }}>Filters</h2>
            {activeFiltersCount > 0 && (
              <span style={{
                background: '#667eea',
                color: 'white',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: 600
              }}>
                {activeFiltersCount} active
              </span>
            )}
          </div>
          <button
            onClick={handleResetFilters}
            style={{
              padding: '8px 16px',
              background: '#fed7d7',
              color: '#c53030',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '12px'
            }}
          >
            Reset Filters
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#4a5568' }}>
              Employee
            </label>
            <select
              value={filters.employee}
              onChange={(e) => handleFilterChange('employee', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px',
                background: 'white'
              }}
            >
              <option value="">All Employees</option>
              {employees.map(emp => (
                <option key={emp.id || emp._id} value={emp.id || emp._id}>
                  {emp.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#4a5568' }}>
              Site
            </label>
            <select
              value={filters.site}
              onChange={(e) => handleFilterChange('site', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px',
                background: 'white'
              }}
            >
              <option value="">All Sites</option>
              {sites.map(site => (
                <option key={site.id || site._id} value={site.id || site._id}>
                  {site.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#4a5568' }}>
              Location
            </label>
            <select
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px',
                background: 'white'
              }}
            >
              <option value="">All Locations</option>
              {locations.map(loc => (
                <option key={loc.id || loc._id} value={loc.name}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#4a5568' }}>
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px',
                background: 'white'
              }}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_transfer">In Transfer</option>
              <option value="received">Received</option>
              <option value="issue_reported">Issue Reported</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#4a5568' }}>
              Date From
            </label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#4a5568' }}>
              Date To
            </label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px'
              }}
            />
          </div>

          <div style={{ gridColumn: 'span 2' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#4a5568' }}>
              Search
            </label>
            <input
              type="text"
              placeholder="Search by asset tag, description, or employee..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px'
              }}
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summaryCards}

      {/* Table */}
      <div style={{
        background: 'white',
        padding: '16px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <HandoversTable
          handovers={handovers}
          loading={loading}
          onRowClick={handleRowClick}
          onSendReminder={handleSendReminder}
          formatDate={formatDate}
          formatDateTime={formatDateTime}
          getStatusLabel={getStatusLabel}
        />
        {handovers.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalHandovers}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(newItemsPerPage) => {
              setItemsPerPage(newItemsPerPage);
              setCurrentPage(1);
            }}
          />
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedHandover && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setShowDetailModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#2d3748' }}>
                Handover Details
              </h2>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowDetailModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>Employee</label>
                <div style={{ fontSize: '14px', color: '#2d3748' }}>{selectedHandover.assignedTo?.name || 'N/A'}</div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>Asset Tag</label>
                <div style={{ fontSize: '14px', color: '#2d3748', fontWeight: 600 }}>{selectedHandover.asset?.assetTag || 'N/A'}</div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>Description</label>
                <div style={{ fontSize: '14px', color: '#4a5568' }}>{selectedHandover.asset?.description || 'N/A'}</div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>Assigned Date</label>
                <div style={{ fontSize: '14px', color: '#4a5568' }}>{formatDateTime(selectedHandover.assignedDate)}</div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>Assigned By</label>
                <div style={{ fontSize: '14px', color: '#4a5568' }}>{selectedHandover.assignedBy?.name || 'N/A'}</div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>Status</label>
                <div>
                  <BadgeStatus
                    status={getStatusLabel(selectedHandover.acknowledgementStatus, selectedHandover.issues, selectedHandover.handoverType, selectedHandover.asset?.status)}
                    variant={
                      getStatusLabel(selectedHandover.acknowledgementStatus, selectedHandover.issues, selectedHandover.handoverType, selectedHandover.asset?.status) === 'Received' ? 'info' :
                        getStatusLabel(selectedHandover.acknowledgementStatus, selectedHandover.issues, selectedHandover.handoverType, selectedHandover.asset?.status) === 'pending' || getStatusLabel(selectedHandover.acknowledgementStatus, selectedHandover.issues, selectedHandover.handoverType, selectedHandover.asset?.status) === 'In Transfer' ? 'warning' :
                          'danger'
                    }
                  />
                </div>
              </div>
              {selectedHandover.acknowledgedAt && (
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>Acknowledgement Date</label>
                  <div style={{ fontSize: '14px', color: '#4a5568' }}>{formatDateTime(selectedHandover.acknowledgedAt)}</div>
                </div>
              )}
              {selectedHandover.comments && (
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>Employee Comment</label>
                  <div style={{ fontSize: '14px', color: '#4a5568', padding: '12px', background: '#f7fafc', borderRadius: '6px' }}>
                    {selectedHandover.comments}
                  </div>
                </div>
              )}
              {selectedHandover.issues && (
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>Issue Reported</label>
                  <div style={{ fontSize: '14px', color: '#e53e3e', padding: '12px', background: '#fed7d7', borderRadius: '6px' }}>
                    {selectedHandover.issues}
                  </div>
                </div>
              )}
              {selectedHandover.notes && (
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>Admin Notes</label>
                  <div style={{ fontSize: '14px', color: '#4a5568', padding: '12px', background: '#f7fafc', borderRadius: '6px' }}>
                    {selectedHandover.notes}
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              {getStatusLabel(selectedHandover.acknowledgementStatus, selectedHandover.issues, selectedHandover.handoverType, selectedHandover.asset?.status) !== 'Received' && (
                <button
                  onClick={() => handleSendReminder(selectedHandover)}
                  style={{
                    padding: '10px 20px',
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '13px'
                  }}
                >
                  📧 Send Reminder
                </button>
              )}
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#e2e8f0',
                  color: '#4a5568',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '13px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcknowledgementsPage;
